import { Link } from 'react-router-dom';
import { coverDescription, coverLabel, sortByCover } from '@/utils/coverTypes';

/**
 * What a public quote START told the visitor, drawn on the ONE final page.
 *
 * Owner, 24 Sep: Motor, Health and Travel follow exactly the same flow --
 * chooser, the line's start form, then the SAME page, `/public/quotes/<token>`
 * (`PublicQuote`), for all three. The start forms therefore do not draw a page
 * of their own after the request is made: they navigate to the quote page and
 * hand it the start response in router state, and the quote page draws these
 * notes above the options. A reload or a shared link has no router state and
 * simply shows the quote page, which is complete on its own.
 *
 * Everything here is the server's own words. No premium is computed here.
 */

/** `start_public_quote` / `start_public_line_quote`'s explicit key list. No
 *  broker id, no lead id, no commission. */
export interface StartResponse {
  public_token: string;
  quote_url: string;
  request_number: string;
  broker_name: string;
  pricing: {
    /** `pending`: a repeat of a request still pricing server-side.
     *  `advisor_quoting`: no insurer prices this line online (health, travel),
     *  so the advisor prepares the options by hand. */
    status: 'priced' | 'no_adapter' | 'no_prices' | 'not_attempted' | 'pending' | 'advisor_quoting' | 'pricing';
    /** Progressive pricing: the insurer x cover still being asked. */
    pending?: { insurer_code: string; insurer_name: string; cover_type: string }[];
    reason?: string;
    quotes_created: number;
    insurers_queried: number;
    product_line: string | null;
    failures: { insurer_code?: string; error_code?: string; message?: string }[];
    /** Plain sentences a visitor can read (`public_pricing_explanations`).
     *  Preferred over `failures`, whose messages are the adapters' own words. */
    explanations?: {
      insurer_code: string;
      insurer_name: string;
      reason: string;
      cover_type?: string | null;
      cover_label?: string | null;
    }[];
    /** The options themselves, read back from the persisted rows. */
    options?: {
      quote_id: string;
      quote_number: string;
      insurer_name: string;
      insurer_code: string;
      cover_type?: string | null;
      cover_label?: string | null;
      total_premium: string;
      gross_premium: string;
      od_premium?: string | null;
      tp_premium?: string | null;
      idv: string | null;
      valid_until: string | null;
    }[];
    detail: string;
  };
  contact_verification: {
    status: 'available' | 'unavailable';
    channel?: string | null;
    detail: string;
  };
  broker_followup: { required: boolean; detail: string };
}

/** What the server says to a repeat start that is not the original attempt:
 *  the request is already in, and nothing that opens it is handed over. */
export interface AlreadyReceivedResponse {
  status: 'already_received';
  detail: string;
}

export const DUPLICATE_FALLBACK = 'We already have your request -- your advisor will contact you.';

export function isStartResponse(data: unknown): data is StartResponse {
  return !!data && typeof (data as StartResponse).public_token === 'string' && !!(data as StartResponse).public_token;
}

/**
 * The per-attempt key a start form sends (pre-deploy review, 24 Sep).
 *
 * The server hands an existing quote's link back to a repeat start ONLY when
 * it presents the key the first start sent -- so a stranger who knows a
 * visitor's phone cannot fetch their quote. Made on the first submit and kept
 * in sessionStorage for this form, so this tab's retry (a timeout, a double
 * click) still gets its own quote back. Storage may be unavailable (private
 * mode): the key then lives only for this call, and a retry is simply told
 * the request is already in.
 */
export function attemptKeyFor(form: string): string {
  const storageKey = `public-quote-attempt:${form}`;
  try {
    const existing = window.sessionStorage.getItem(storageKey);
    if (existing) return existing;
  } catch {
    // storage unavailable -- fall through to a fresh key
  }
  let key = '';
  try {
    key = window.crypto?.randomUUID?.() ?? '';
  } catch {
    key = '';
  }
  if (!key) {
    const bytes = new Uint8Array(16);
    window.crypto?.getRandomValues?.(bytes);
    key = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('') || `${Date.now()}-${Math.random()}`;
  }
  try {
    window.sessionStorage.setItem(storageKey, key);
  } catch {
    // storage unavailable
  }
  return key;
}

/** Router state the start forms hand the quote page. */
export interface QuotePageState {
  start?: StartResponse;
}

/** The start response in router state, but only for THIS token -- state that
 *  belongs to another quote is ignored rather than drawn on the wrong page. */
export function startFromState(state: unknown, token: string | undefined): StartResponse | null {
  const start = (state as QuotePageState | null)?.start;
  if (!start || typeof start !== 'object' || !token || start.public_token !== token) return null;
  return start;
}

export function quotePagePath(token: string): string {
  return `/public/quotes/${token}`;
}

export function StartPanel({
  tone,
  title,
  children,
}: {
  tone: 'neutral' | 'good' | 'warn';
  title: string;
  children: React.ReactNode;
}) {
  const tones = {
    neutral:
      'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white',
    good: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-900 dark:text-green-200',
    warn: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-900 dark:text-amber-200',
  } as const;
  return (
    <section className={`mb-6 rounded-xl border p-6 shadow-sm ${tones[tone]}`}>
      <h2 className="text-lg font-semibold">{title}</h2>
      <div className="mt-2 text-sm">{children}</div>
    </section>
  );
}

/** The options the START response carried. Drawn only when the quote page
 *  cannot load, so a premium the visitor was already sent is never withheld. */
export function StartOptions({ options }: { options: NonNullable<StartResponse['pricing']['options']> }) {
  if (options.length === 0) return null;
  return (
    <ul className="space-y-2">
      {sortByCover(options).map((option) => (
        <li
          key={option.quote_id}
          className="flex items-baseline justify-between gap-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3"
        >
          <div>
            <p className="font-medium text-gray-900 dark:text-white">
              {coverLabel(option) ? `${coverLabel(option)} — ${option.insurer_name}` : option.insurer_name}
            </p>
            {coverDescription(option.cover_type) && (
              <p className="text-xs text-gray-600 dark:text-gray-400">{coverDescription(option.cover_type)}</p>
            )}
            {option.idv && <p className="text-xs text-gray-600 dark:text-gray-400">IDV ₹ {option.idv}</p>}
          </div>
          <p className="text-lg font-semibold whitespace-nowrap text-gray-900 dark:text-white">
            ₹ {option.total_premium}
          </p>
        </li>
      ))}
    </ul>
  );
}

/** The start's own account, exactly as the server reported it. */
export function StartNotes({ start }: { start: StartResponse }) {
  const { pricing, contact_verification: verification, broker_followup: followup } = start;
  const reasons =
    pricing.explanations && pricing.explanations.length > 0
      ? pricing.explanations.map((note) => note.reason)
      : (pricing.failures ?? []).map(
          (failure) =>
            `${failure.insurer_code ? `${failure.insurer_code}: ` : ''}${
              failure.message || failure.error_code || 'No reason given.'
            }`
        );
  const reasonList =
    reasons.length > 0 ? (
      <ul className="mt-3 list-disc space-y-1 pl-5">
        {reasons.map((reason, index) => (
          <li key={index}>{reason}</li>
        ))}
      </ul>
    ) : null;

  return (
    <div data-testid="start-notes">
      <header className="mb-6">
        <p className="text-xs font-semibold uppercase tracking-wider text-primary-600 dark:text-primary-400">
          Request {start.request_number}
        </p>
        <h2 className="mt-2 text-2xl font-bold text-gray-900 dark:text-white">
          Your quote request is with {start.broker_name}
        </h2>
      </header>

      {pricing.status === 'pricing' ? null : pricing.status === 'pending' ? (
        <StartPanel tone="neutral" title="Still fetching live prices">
          <p>{pricing.detail}</p>
        </StartPanel>
      ) : pricing.status === 'priced' ? (
        <StartPanel tone="good" title={`${pricing.quotes_created} option(s) priced`}>
          <p>{pricing.detail}</p>
          {reasonList}
        </StartPanel>
      ) : pricing.status === 'advisor_quoting' ? (
        <StartPanel tone="neutral" title="Your advisor is preparing quotes">
          <p>{pricing.detail}</p>
        </StartPanel>
      ) : (
        <StartPanel tone="warn" title="No premium could be fetched yet">
          <p>{pricing.detail}</p>
          {reasonList}
          <p className="mt-3">
            Nothing here is an estimate. When a premium appears on your quote page it came from the
            insurer.
          </p>
        </StartPanel>
      )}

      {/* Board #259: no delivery channel exists, so nothing claims a code was
          sent. When a provider is configured the server answers `available`. */}
      <StartPanel
        tone={verification.status === 'available' ? 'neutral' : 'warn'}
        title={verification.status === 'available' ? 'Verifying your number' : 'We could not verify your number'}
      >
        <p>{verification.detail}</p>
      </StartPanel>

      {/* Board #236: a publicly originated quote has no linked contact until a
          human at the broker attaches one. Said out loud. */}
      {followup.required && (
        <StartPanel tone="neutral" title="Before a policy can be issued">
          <p>{followup.detail}</p>
        </StartPanel>
      )}

      <p className="mb-6 text-sm text-gray-600 dark:text-gray-400">
        <Link
          to={quotePagePath(start.public_token)}
          className="font-medium text-primary-600 hover:text-primary-700"
        >
          Save the link to your quote
        </Link>{' '}
        -- it is where your options, documents and policy will appear, even after you close this
        page.
      </p>
    </div>
  );
}
